import React, { useState } from 'react';
import { Plus, Utensils, Database, CheckCircle, X, ScanLine } from 'lucide-react';
import { useFoodEntries } from '../../hooks/useSupabaseData';
import { localUsdaApi, type LocalUSDAFood as USDAFood } from '../../lib/localUsdaApi';
import { type BarcodeProduct } from '../../lib/barcodeApi';
import DataDisclaimer from '../common/DataDisclaimer';
import BarcodeScanner from '../barcode/BarcodeScanner';
import { ErrorBoundary } from '../common/ErrorBoundary';

export const EnhancedFoodTracker: React.FC = () => {
  const { foodEntries, loading, error, addFoodEntry } = useFoodEntries();
  const [showAddForm, setShowAddForm] = useState(false);

  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [usdaSearchQuery, setUSDASearchQuery] = useState('');
  const [usdaResults, setUSDAResults] = useState<USDAFood[]>([]);
  const [usdaSearchLoading, setUSDASearchLoading] = useState(false);
  const [selectedUSDAFood, setSelectedUSDAFood] = useState<USDAFood | null>(null);
  const [selectedBarcodeProduct, setSelectedBarcodeProduct] = useState<BarcodeProduct | null>(null);

  const [formData, setFormData] = useState({
    meal_type: 'breakfast',
    food_name: '',
    brand: '',
    serving_size: 1,
    serving_unit: '',
    macros: {
      calories: 0,
      carbs: 0,
      protein: 0,
      fat: 0,
      fiber: 0
    },
    logged_at: new Date().toISOString()
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('macros.')) {
      const macroKey = name.split('.')[1];
      let numValue = parseFloat(value) || 0;
      // Round to 2 decimal places to prevent validation issues
      numValue = Math.round(numValue * 100) / 100;
      setFormData(prev => ({
        ...prev,
        macros: {
          ...prev.macros,
          [macroKey]: numValue
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'serving_size' ? parseFloat(value) || 0 : value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = await addFoodEntry(formData);
    if (result) {
      resetForm();
    }
  };

  const searchUSDAFoods = async () => {
    if (!usdaSearchQuery.trim()) return;
    
    setUSDASearchLoading(true);
    try {
      const result = await localUsdaApi.searchFoods(usdaSearchQuery, 1, 20);
      
      // Validate and sanitize the results to prevent React errors
      const validatedResults = result.results.filter(food => {
        if (!food || typeof food !== 'object') {
          console.warn('Invalid food object found:', food);
          return false;
        }
        
        // Ensure all required fields are present and safe
        if (!food.fdc_id || !food.description) {
          console.warn('Food missing required fields:', food);
          return false;
        }
        
        // Ensure description is a string
        if (typeof food.description !== 'string') {
          console.warn('Food description is not a string:', food.description);
          return false;
        }
        
        return true;
      }).map(food => ({
        ...food,
        description: String(food.description || ''),
        brand_name: food.brand_name ? String(food.brand_name) : null,
        category: food.category ? String(food.category) : null,
        data_type: food.data_type ? String(food.data_type) : null,
        fdc_id: Number(food.fdc_id) || 0
      }));
      
      setUSDAResults(validatedResults);
    } catch (error) {
      console.error('USDA search error:', error);
      setUSDAResults([]);
    } finally {
      setUSDASearchLoading(false);
    }
  };

  const selectUSDAFood = (food: USDAFood) => {
    setSelectedUSDAFood(food);
    
    // Safety check to ensure food object is valid
    if (!food || typeof food !== 'object') {
      console.error('Invalid food object:', food);
      return;
    }
    
    const macros = localUsdaApi.extractMacros(food);
    
    // Debug logging to see what's happening
    console.log('Selected USDA Food:', food);
    console.log('Food nutrients:', food.nutrients);
    console.log('Extracted macros:', macros);
    
    setFormData(prev => ({
      ...prev,
      food_name: food.description || '',
      brand: (food.brand_name || food.brand_owner || '').toString(),
      serving_size: food.serving_size || 100,
      serving_unit: food.serving_size_unit || 'g',
      macros: macros
    }));
  };

  const handleBarcodeProductFound = (product: BarcodeProduct) => {
    setSelectedBarcodeProduct(product);
    
    setFormData(prev => ({
      ...prev,
      food_name: product.name,
      brand: product.brand || '',
      serving_size: product.nutrition?.serving_size || 100,
      serving_unit: product.nutrition?.serving_unit || 'g',
      macros: {
        calories: product.nutrition?.calories || 0,
        carbs: product.nutrition?.carbs || 0,
        protein: product.nutrition?.protein || 0,
        fat: product.nutrition?.fat || 0,
        fiber: product.nutrition?.fiber || 0
      }
    }));
    setShowBarcodeScanner(false);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      meal_type: 'breakfast',
      food_name: '',
      brand: '',
      serving_size: 1,
      serving_unit: '',
      macros: {
        calories: 0,
        carbs: 0,
        protein: 0,
        fat: 0,
        fiber: 0
      },
      logged_at: new Date().toISOString()
    });
    setSelectedUSDAFood(null);
    setSelectedBarcodeProduct(null);
    setShowAddForm(false);
    setShowBarcodeScanner(false);
    setUSDAResults([]);
    setUSDASearchQuery('');
  };

  const getTodaysFoodEntries = () => {
    const today = new Date().toDateString();
    return foodEntries.filter(entry => 
      new Date(entry.logged_at).toDateString() === today
    );
  };

  const getTotalMacros = () => {
    const todaysEntries = getTodaysFoodEntries();
    return todaysEntries.reduce((total, entry) => ({
      calories: total.calories + entry.macros.calories,
      carbs: total.carbs + entry.macros.carbs,
      protein: total.protein + entry.macros.protein,
      fat: total.fat + entry.macros.fat,
      fiber: total.fiber + entry.macros.fiber
    }), { calories: 0, carbs: 0, protein: 0, fat: 0, fiber: 0 });
  };

  const getMealEntries = (mealType: string) => {
    const today = new Date().toDateString();
    return foodEntries.filter(entry => 
      entry.meal_type === mealType &&
      new Date(entry.logged_at).toDateString() === today
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const totalMacros = getTotalMacros();

  return (
    <ErrorBoundary>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Food Tracking</h2>
            <p className="text-gray-600">Track your nutrition with comprehensive database</p>
            <DataDisclaimer type="food" compact className="mt-1" />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowBarcodeScanner(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
            >
              <ScanLine className="w-4 h-4" />
              Scan Barcode
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Log Food
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="text-red-800">
              <strong>Error:</strong> {error.toString()}
            </div>
          </div>
        )}

        {/* Daily Macros Summary */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-blue-600 text-sm font-medium">Calories</div>
            <div className="text-2xl font-bold text-blue-900">{Math.round(totalMacros.calories)}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-green-600 text-sm font-medium">Carbs (g)</div>
            <div className="text-2xl font-bold text-green-900">{Math.round(totalMacros.carbs)}</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-purple-600 text-sm font-medium">Protein (g)</div>
            <div className="text-2xl font-bold text-purple-900">{Math.round(totalMacros.protein)}</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-yellow-600 text-sm font-medium">Fat (g)</div>
            <div className="text-2xl font-bold text-yellow-900">{Math.round(totalMacros.fat)}</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-orange-600 text-sm font-medium">Fiber (g)</div>
            <div className="text-2xl font-bold text-orange-900">{Math.round(totalMacros.fiber)}</div>
          </div>
        </div>

        {/* Add Food Form */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">Log Food Entry</h3>
                <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* USDA Search Section */}
              <div className="mb-6 p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Database className="w-5 h-5 text-green-600" />
                  <h4 className="font-medium text-green-900">Search USDA Food Database</h4>
                </div>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={usdaSearchQuery}
                    onChange={(e) => setUSDASearchQuery(e.target.value)}
                    placeholder="Search for food (e.g., Apple, Chicken breast, Oatmeal)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    onKeyPress={(e) => e.key === 'Enter' && searchUSDAFoods()}
                  />
                  <button
                    onClick={searchUSDAFoods}
                    disabled={usdaSearchLoading}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {usdaSearchLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>

                {selectedUSDAFood && (
                  <div className="bg-green-100 border border-green-300 rounded-md p-3 mb-3">
                    <div className="flex items-center gap-2 text-green-800">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium">Selected from USDA Database:</span>
                    </div>
                    <div className="mt-1 text-green-700">
                      <strong>{selectedUSDAFood.description}</strong>
                      {selectedUSDAFood.brand_name && <span> - {selectedUSDAFood.brand_name}</span>}
                      <div className="text-sm mt-1">
                        Category: {selectedUSDAFood.category} | Data Type: {selectedUSDAFood.data_type} | FDC ID: {selectedUSDAFood.fdc_id}
                      </div>
                    </div>
                  </div>
                )}

                {selectedBarcodeProduct && (
                  <div className="bg-blue-100 border border-blue-300 rounded-md p-3 mb-3">
                    <div className="flex items-center gap-2 text-blue-800">
                      <ScanLine className="w-4 h-4" />
                      <span className="font-medium">Scanned Product:</span>
                    </div>
                    <div className="mt-1 text-blue-700">
                      <strong>{selectedBarcodeProduct.name}</strong>
                      {selectedBarcodeProduct.brand && <span> - {selectedBarcodeProduct.brand}</span>}
                      <div className="text-sm mt-1">
                        Barcode: {selectedBarcodeProduct.barcode} | Source: Barcode Database
                      </div>
                    </div>
                  </div>
                )}

                {usdaResults.length > 0 && (
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                    {usdaResults.map((food) => {
                      // Safety check to ensure food object is valid
                      if (!food || typeof food !== 'object') {
                        console.error('Invalid food object in results:', food);
                        return null;
                      }
                      
                      return (
                        <button
                          key={food.fdc_id || Math.random()}
                          onClick={() => selectUSDAFood(food)}
                          className="w-full text-left p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{food.description || 'Unknown Food'}</div>
                          {food.brand_name && (
                            <div className="text-sm text-gray-600">Brand: {food.brand_name}</div>
                          )}
                          <div className="text-sm text-gray-500">
                            {food.category && <span>{food.category}</span>}
                            {food.data_type && <span> • {food.data_type}</span>}
                            <span> • FDC ID: {food.fdc_id || 'N/A'}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meal Type *
                    </label>
                    <select
                      name="meal_type"
                      value={formData.meal_type}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="breakfast">Breakfast</option>
                      <option value="lunch">Lunch</option>
                      <option value="dinner">Dinner</option>
                      <option value="snack">Snack</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Food Name *
                    </label>
                    <input
                      type="text"
                      name="food_name"
                      value={formData.food_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter food name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Brand (Optional)
                    </label>
                    <input
                      type="text"
                      name="brand"
                      value={formData.brand}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Brand name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Serving Size *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        name="serving_size"
                        value={formData.serving_size}
                        onChange={handleInputChange}
                        required
                        min="0"
                        step="0.1"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        name="serving_unit"
                        value={formData.serving_unit}
                        onChange={handleInputChange}
                        placeholder="unit"
                        className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Macros Section */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Nutritional Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Calories
                      </label>
                      <input
                        type="number"
                        name="macros.calories"
                        value={formData.macros.calories}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Carbs (g)
                      </label>
                      <input
                        type="number"
                        name="macros.carbs"
                        value={formData.macros.carbs}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Protein (g)
                      </label>
                      <input
                        type="number"
                        name="macros.protein"
                        value={formData.macros.protein}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fat (g)
                      </label>
                      <input
                        type="number"
                        name="macros.fat"
                        value={formData.macros.fat}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fiber (g)
                      </label>
                      <input
                        type="number"
                        name="macros.fiber"
                        value={formData.macros.fiber}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Log Food
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Meals Display */}
        <div className="space-y-6">
          {['breakfast', 'lunch', 'dinner', 'snack'].map((mealType) => {
            const mealEntries = getMealEntries(mealType);
            const mealMacros = mealEntries.reduce((total, entry) => ({
              calories: total.calories + entry.macros.calories,
              carbs: total.carbs + entry.macros.carbs,
              protein: total.protein + entry.macros.protein,
              fat: total.fat + entry.macros.fat,
              fiber: total.fiber + entry.macros.fiber
            }), { calories: 0, carbs: 0, protein: 0, fat: 0, fiber: 0 });

            return (
              <div key={mealType} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 capitalize">{mealType}</h3>
                  <div className="text-sm text-gray-600">
                    {Math.round(mealMacros.calories)} calories
                  </div>
                </div>
                
                {mealEntries.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    No {mealType} logged yet
                  </div>
                ) : (
                  <div className="space-y-3">
                    {mealEntries.map((entry) => (
                      <div key={entry.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                        <div>
                          <div className="font-medium text-gray-900">{entry.food_name}</div>
                          {entry.brand && (
                            <div className="text-sm text-gray-600">{entry.brand}</div>
                          )}
                          <div className="text-sm text-gray-500">
                            {entry.serving_size} {entry.serving_unit}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-gray-900">
                            {Math.round(entry.macros.calories)} cal
                          </div>
                          <div className="text-sm text-gray-600">
                            C: {Math.round(entry.macros.carbs)}g | 
                            P: {Math.round(entry.macros.protein)}g | 
                            F: {Math.round(entry.macros.fat)}g
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {foodEntries.length === 0 && (
          <div className="text-center py-12">
            <Utensils className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No food entries yet</h3>
            <p className="text-gray-500 mb-4">Start tracking your nutrition with our USDA database</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Log Your First Meal
            </button>
          </div>
        )}

        {/* Barcode Scanner */}
        <BarcodeScanner
          isOpen={showBarcodeScanner}
          onProductFound={handleBarcodeProductFound}
          onClose={() => setShowBarcodeScanner(false)}
        />
      </div>
    </ErrorBoundary>
  );
};
